import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const serviceLinks = [
  { name: "Interior Painting", path: "/services/interior-painting" },
  { name: "Exterior Painting", path: "/services/exterior-painting" },
  { name: "Cabinet Painting & Refinishing", path: "/services/cabinet-painting" },
  { name: "Deck & Fence Staining", path: "/services/deck-fence-staining" },
  { name: "Drywall Repair & Prep", path: "/services/drywall-repair" },
  { name: "Color Consultation", path: "/services/color-consultation" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => { setIsOpen(false); setServicesOpen(false); }, [location]);

  const isServicePage = location.pathname.startsWith("/services");

  return (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="navbar">
      {/* Top Bar */}
      <div className={`w-full h-[40px] overflow-hidden transition-all duration-300 ${scrolled ? 'hidden' : 'hidden md:flex'}`}>
        <div className="flex-1 bg-white"></div>
        <div className="relative flex items-center bg-[#0a2540] text-white" style={{ width: '50%', paddingLeft: '60px', paddingRight: '40px', gap: '24px' }}>
          <div className="absolute top-0" style={{ left: '-38px', width: 0, height: 0, borderTop: '40px solid #0a2540', borderLeft: '40px solid transparent' }}></div>
          <div className="flex items-center gap-3 whitespace-nowrap text-[14px] font-medium">
            <Mail className="w-4 h-4 text-[#d4a017]" strokeWidth={2} />
            <span>info@vanguardpm.com</span>
          </div>
          <div className="w-px h-6 bg-white/40 flex-shrink-0"></div>
          <div className="flex items-center gap-3 whitespace-nowrap text-[14px] font-medium">
            <Phone className="w-4 h-4 text-[#d4a017]" strokeWidth={2} />
            <span>(236)868-1195</span>
          </div>
          <div className="w-px h-6 bg-white/40 flex-shrink-0"></div>
          <div className="flex items-center gap-4">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-300 transition-colors" aria-label="Instagram">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href="mailto:info@vanguardpm.com" className="text-white hover:text-gray-300 transition-colors" aria-label="Email">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <nav className={`bg-white transition-all duration-300 relative ${scrolled ? 'shadow-lg' : ''}`}>
        <Link to="/" className={`absolute left-6 sm:left-10 lg:left-14 z-50 transition-all duration-300 ${scrolled ? 'top-1/2 -translate-y-1/2' : '-top-5'}`} data-testid="logo-link">
          <img src="/assets/images/logo-banner.png" alt="VanguardPM" className={`w-auto transition-all duration-300 ${scrolled ? 'h-14' : 'h-20'}`} />
        </Link>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end py-4 pl-48">
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/" data-testid="nav-home" className={`text-sm font-semibold transition-colors ${location.pathname === '/' ? 'text-[#f97316]' : 'text-[#1a2a44] hover:text-[#f97316]'}`}>HOME</Link>
              <Link to="/about" data-testid="nav-about-us" className={`text-sm font-semibold transition-colors ${location.pathname === '/about' ? 'text-[#f97316]' : 'text-[#1a2a44] hover:text-[#f97316]'}`}>ABOUT US</Link>
              
              {/* Services Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  data-testid="nav-service"
                  className={`text-sm font-semibold transition-colors flex items-center gap-1 ${isServicePage ? 'text-[#f97316]' : 'text-[#1a2a44] hover:text-[#f97316]'}`}
                >
                  SERVICE <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesOpen && (
                  <div className="absolute top-full left-0 mt-3 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50" data-testid="services-dropdown">
                    {serviceLinks.map((s) => (
                      <Link
                        key={s.path}
                        to={s.path}
                        className={`block px-5 py-3 text-sm font-medium transition-colors hover:bg-orange-50 hover:text-[#f97316] ${location.pathname === s.path ? 'text-[#f97316] bg-orange-50' : 'text-[#1a2a44]'}`}
                        data-testid={`dropdown-${s.path.split('/').pop()}`}
                      >
                        {s.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/portfolio" data-testid="nav-portfolio" className={`text-sm font-semibold transition-colors ${location.pathname === '/portfolio' ? 'text-[#f97316]' : 'text-[#1a2a44] hover:text-[#f97316]'}`}>PORTFOLIO</Link>
              <Link to="/contact" data-testid="nav-contact-us" className={`text-sm font-semibold transition-colors ${location.pathname === '/contact' ? 'text-[#f97316]' : 'text-[#1a2a44] hover:text-[#f97316]'}`}>CONTACT US</Link>

              <Link to="/contact">
                <Button className="bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-6 rounded-sm text-sm ml-4" data-testid="get-started-btn">Get Started</Button>
              </Link>
            </div>

            <button className="lg:hidden w-12 h-12 rounded-lg bg-[#f97316] flex items-center justify-center text-white" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed inset-0 top-0 bg-white transition-transform duration-500 ease-out z-50 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`} data-testid="mobile-menu">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b">
            <Link to="/" className="flex items-center" onClick={() => setIsOpen(false)}>
              <img src="/assets/images/logo-banner.png" alt="VanguardPM" className="h-8 w-auto" />
            </Link>
            <button className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center" onClick={() => setIsOpen(false)}>
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="flex flex-col p-8 gap-4 flex-1 overflow-y-auto">
            <Link to="/" onClick={() => setIsOpen(false)} className={`text-xl font-bold ${location.pathname === '/' ? 'text-[#f97316]' : 'text-[#1a2a44]'}`}>HOME</Link>
            <Link to="/about" onClick={() => setIsOpen(false)} className={`text-xl font-bold ${location.pathname === '/about' ? 'text-[#f97316]' : 'text-[#1a2a44]'}`}>ABOUT US</Link>
            
            {/* Mobile Services Accordion */}
            <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className={`text-xl font-bold flex items-center gap-2 ${isServicePage ? 'text-[#f97316]' : 'text-[#1a2a44]'}`}>
              SERVICE <ChevronDown className={`w-5 h-5 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 space-y-3">
                {serviceLinks.map((s) => (
                  <Link key={s.path} to={s.path} onClick={() => setIsOpen(false)} className={`block text-base font-medium ${location.pathname === s.path ? 'text-[#f97316]' : 'text-gray-600 hover:text-[#f97316]'}`}>
                    {s.name}
                  </Link>
                ))}
              </div>
            )}

            <Link to="/portfolio" onClick={() => setIsOpen(false)} className={`text-xl font-bold ${location.pathname === '/portfolio' ? 'text-[#f97316]' : 'text-[#1a2a44]'}`}>PORTFOLIO</Link>
            <Link to="/contact" onClick={() => setIsOpen(false)} className={`text-xl font-bold ${location.pathname === '/contact' ? 'text-[#f97316]' : 'text-[#1a2a44]'}`}>CONTACT US</Link>
          </div>
          <div className="p-8 border-t space-y-4">
            <div className="flex flex-col gap-3 text-sm text-gray-600 mb-4">
              <a href="mailto:info@vanguardpm.com" className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#f97316]" /> info@vanguardpm.com</a>
              <a href="tel:2368681195" className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#f97316]" /> (236)868-1195</a>
            </div>
            <Link to="/contact" onClick={() => setIsOpen(false)}>
              <Button className="w-full bg-[#f97316] hover:bg-[#ea580c] text-white font-bold py-6 text-lg rounded-sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
